import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import SortedTooltip from './SortedTooltip';
import ChartEmptyState from './ChartEmptyState';
import InfoTooltip from './InfoTooltip';

export interface BarSeries {
  key: string;
  color: string;
}

interface Props {
  title: string;
  data: Record<string, string | number>[];
  xKey: string;
  bars: BarSeries[];
  height?: number;
  allowDecimals?: boolean;
  showLegend?: boolean;
  headerRight?: React.ReactNode;
  info?: string;
}

const SimpleBarChartCard = ({
  title,
  data,
  xKey,
  bars,
  height = 220,
  allowDecimals = true,
  showLegend = false,
  headerRight,
  info,
}: Props) => {
  const hasValues = data?.some((row) => bars.some((bar) => Number(row[bar.key]) > 0));

  return (
    <div className="bg-white rounded-lg shadow p-4 w-full">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
          {info && <InfoTooltip text={info} />}
        </div>
        {headerRight}
      </div>
      {!data?.length || !hasValues ? (
        <ChartEmptyState height={height} />
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey={xKey} tick={{ fontSize: 12 }} />
            <YAxis allowDecimals={allowDecimals} tick={{ fontSize: 12 }} />
            <Tooltip content={<SortedTooltip />} />
            {showLegend && <Legend />}
            {bars.map((bar) => (
              <Bar key={bar.key} dataKey={bar.key} fill={bar.color} radius={[ 4, 4, 0, 0 ]} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default SimpleBarChartCard;
